<template>
  <section class="reports-cards">
    <div class="container">
      <div class="reports-cards__layout">
        <div class="reports-cards__filters">
          <ReportsFilters class="reports-cards__filters-desktop" />
          <ReportsFiltersMobile class="reports-cards__filters-mobile" />
        </div>
        <section class="reports-cards__content">
          <section class="reports-cards__container">
            <div class="reports-cards__header">
              <div class="reports-cards__number">
                {{ filteredReports.length }}
                {{
                  filteredReports.length === 1
                    ? $t('common.reports.result')
                    : $t('common.reports.results')
                }}
              </div>
              <button class="reports-cards__show-all" @click="onClearFilters">
                {{ $t('common.reports.show_all') }}
              </button>
            </div>
            <TransitionGroup tag="div" class="reports-cards__list" name="list">
              <ReportsCard
                v-for="report in filteredReports"
                :key="report.uuid"
                :report="report"
                class="reports-cards__card"
              />
            </TransitionGroup>
          </section>
          <div
            v-if="filteredReports.length === 0"
            class="reports-cards__no-results"
          >
            <img
              width="74"
              height="74"
              src="~/assets/icons/icon-noresults.svg?url"
              alt=""
              class="reports-cards__no-results-image"
            />
            <p class="reports-cards__no-results-info">
              {{ $t('common.reports.no_results') }}
            </p>
            <BaseButton
              :title="$t('common.reset_filters')"
              outlined
              @click="clearFilters"
            >
              <template #text> {{ $t('common.reset_filters') }} </template>
            </BaseButton>
          </div>
        </section>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { SbPageReportStoryblok, SbReportTag } from '@/types'

interface Props {
  reports: SbPageReportStoryblok[]
}

const props = defineProps<Props>()

const reportsStore = useReportsStore()

const { clearFilters } = reportsStore

const { filters, checkedTagsArray } = storeToRefs(reportsStore)

const filteredReports = computed(() => {
  if (!props.reports) {
    return []
  }

  let filteredReports = [...props.reports]
  filteredReports[0].isNew = true

  if (filters.value.shouldHaveEnglish && !filters.value.shouldHaveGerman) {
    filteredReports = filteredReports.filter((report) => {
      return (
        report.content.available_languages.includes('en') &&
        !report.content.available_languages.includes('de')
      )
    })
  }

  if (!filters.value.shouldHaveEnglish && filters.value.shouldHaveGerman) {
    filteredReports = filteredReports.filter((report) => {
      return (
        !report.content.available_languages.includes('en') &&
        report.content.available_languages.includes('de')
      )
    })
  }

  if (filters.value.shouldHaveEnglish && filters.value.shouldHaveGerman) {
    filteredReports = filteredReports.filter((report) => {
      return (
        report.content.available_languages.includes('en') &&
        report.content.available_languages.includes('de')
      )
    })
  }

  if (checkedTagsArray.value && checkedTagsArray.value.length > 0) {
    filteredReports = filteredReports.filter((report) => {
      const reportTags = report.content.tags.map((tag: SbReportTag) => tag.uuid)
      return checkedTagsArray.value.every((tag) => reportTags.includes(tag))
    })
  }

  return filteredReports
})

const onClearFilters = () => {
  clearFilters()
}
</script>

<style lang="scss" scoped>
.reports-cards {
  background-color: var(--paper-100);
  padding: 80px 0;

  @media (min-width: $breakpoint-md) {
    padding: 96px 0 128px;
  }

  &__container {
    display: flex;
    flex-direction: column;
    row-gap: 40px;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  &__number {
    @include heading-subheading;
  }

  &__show-all {
    font-size: var(--font-size-paragraph-2xs);
    line-height: 21px;

    &:hover {
      color: var(--color-link);
    }
  }

  &__no-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 12px;
    margin-top: 36px;
  }

  &__no-results-info {
    @include paragraph-m;

    margin-bottom: 0;
    font-weight: var(--font-weight-paragraph-bold);
  }

  &__cards-section {
    padding: 80px 0;
    background-color: var(--gray-100);

    @media (min-width: $breakpoint-md) {
      padding: 96px 0;
    }
  }

  &__layout {
    display: grid;
    grid-template-columns: 1fr;

    @media (min-width: $breakpoint-lg) {
      grid-template-columns: 214px 1fr;
      column-gap: 40px;
    }
  }

  &__filters-desktop {
    display: none;
    position: sticky;
    top: 120px;

    @media (min-width: $breakpoint-lg) {
      display: block;
    }
  }

  &__filters-mobile {
    display: block;
    margin-bottom: 40px;

    @media (min-width: $breakpoint-lg) {
      display: none;
    }
  }

  &__card {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__list {
    position: relative;
  }
}
</style>
